import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { MaskLines } from "./Reveal";
import { FloorPlan } from "./FloorPlan";
import { scrollToId } from "./Logo";

const ease = [0.22, 1, 0.36, 1];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.2]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section ref={ref} data-testid="hero-section" className="blueprint relative overflow-hidden pt-[72px]">
      <div className="mx-auto grid max-w-[1440px] gap-12 px-5 pb-16 pt-12 sm:px-8 lg:grid-cols-12 lg:gap-8 lg:px-12 lg:pb-24 lg:pt-20">
        <motion.div style={{ y: textY }} className="relative z-10 lg:col-span-7">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.1 }} className="eyebrow flex items-center gap-3" data-testid="hero-eyebrow">
            <span className="inline-block h-px w-10 bg-[var(--clay)]" /> Unternehmensnachfolge · Raum · IT
          </motion.p>
          <h1 data-testid="hero-title" className="font-serif-d mt-8 text-[3.1rem] font-normal leading-[0.95] tracking-[-0.02em] text-[var(--ink)] sm:text-7xl lg:text-[6.6rem] xl:text-[7.4rem]">
            <MaskLines delay={0.25} lines={[
              "Ihr Lebenswerk.",
              <span key="b">Neu gebaut für</span>,
              <span key="c" className="italic text-[var(--clay)]">die nächste</span>,
              <span key="d" className="italic text-[var(--clay)]">Generation.</span>,
            ]} />
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.95, ease }} className="mt-10 max-w-xl text-base leading-relaxed text-[var(--ink-2)] md:text-lg" data-testid="hero-subtitle">
            Wir machen Unternehmen im Mittelstand fit für den Verkauf: mit moderner IT-Infrastruktur und einem Arbeitsplatzkonzept, das Käufer und junge Talente überzeugt. Für einen besseren Preis und eine Übergabe, die gelingt.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 1.1, ease }} className="mt-10 flex flex-wrap items-center gap-4">
            <button data-testid="hero-cta-button" onClick={() => scrollToId("kontakt")} className="group flex items-center gap-3 rounded-full bg-[var(--ink)] py-2 pl-6 pr-2 text-sm text-[var(--stone)] transition-[background-color] duration-300 hover:bg-[var(--clay)]">
              Erstgespräch vereinbaren
              <span className="grid h-10 w-10 place-items-center rounded-full bg-[var(--stone)] text-[var(--ink)] transition-transform duration-500 group-hover:rotate-45"><ArrowUpRight className="h-4 w-4" /></span>
            </button>
            <button data-testid="hero-secondary-button" onClick={() => scrollToId("ansatz")} className="flex items-center gap-2 px-2 py-3 text-sm text-[var(--ink)] underline decoration-[var(--clay)] underline-offset-[6px] transition-colors hover:text-[var(--clay)]">
              Unser Ansatz <ArrowDownRight className="h-4 w-4" />
            </button>
          </motion.div>
        </motion.div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
            animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
            transition={{ duration: 1.4, delay: 0.4, ease }}
            className="relative aspect-[4/5] w-full overflow-hidden rounded-[2px] bg-[var(--slate)] lg:mt-8"
          >
            <motion.img style={{ y: imgY, scale: imgScale }} src="/img/hero.jpg" alt="Moderner, lichtdurchfluteter Büroflur mit Glaswänden" className="h-full w-full object-cover" data-testid="hero-image" />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
            <p className="font-mono-d absolute bottom-5 left-5 text-xs uppercase tracking-[0.2em] text-white/90">Fig. 01 — Arbeitswelt 2030</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 1.2, ease }}
            className="relative z-10 -mt-24 ml-auto w-[78%] sm:-mt-32 sm:w-[62%] lg:absolute lg:-bottom-10 lg:-left-28 lg:mt-0 lg:w-[58%]"
          >
            <FloorPlan />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
